import {Component, effect, EffectCleanupRegisterFn, input, output, signal} from "@angular/core";
import {Translations} from "@translations";
import {ListData} from "@models";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

/**
@deprecated use @common/ui/pagination instead
 */
@Component({
	selector: "sim-pagination-old",
	templateUrl: "./old-pagination.component.html",
	styleUrls: ["./old-pagination.component.scss"],
	imports: [MatProgressSpinner],
})
export class OldPaginationComponent<T> {
	// Readonly
	protected readonly T = Translations;

	// I/O
	public listData = input.required<ListData<T>>();
	public pageSize = input<number>(10);
	/**
	 * Set an interval to automatize page looping. Disables manual page selection.
	 */
	public autoLoopInterval = input<number>(0);
	onPageSelect = output<T[]>();

	// Properties
	public loopIntervalPercentage = signal<number>(100);
	public pageData = new Map<number, T[]>();
	public activePage: number = 0;
	public pageIndices: number[] = [];

	constructor() {
		effect(() => this._calcPageData());
		effect((onCleanup) => this._loopPages(onCleanup));
	}

	public setPage(page: number): void {
		this.activePage = page;
		const activePageData = this.pageData.get(page);
		if (activePageData) {
			this.onPageSelect.emit(activePageData);
		}
	}

	public goPrevious(options?: {allowLoop: boolean}) {
		if (this.activePage > 0) {
			this.setPage(this.activePage - 1);
		} else if (options?.allowLoop) {
			this.setPage(this.listData().pages - 1);
		}
	}

	public goNext(options?: {allowLoop: boolean}) {
		if (this.activePage < this.listData().pages - 1) {
			this.setPage(this.activePage + 1);
		} else if (options?.allowLoop) {
			this.setPage(0);
		}
	}

	private _calcPageData(): void {
		// Create pages
		for (let index = 0; index < this.listData().pages; index++) {
			this.pageData.set(index, this.listData().data.slice(index * this.pageSize(), (index + 1) * this.pageSize()));
		}
		this.pageIndices = Array.from({length: this.listData().pages}, (_, i) => i);
		this.setPage(this.activePage);
	}

	private _loopPages(onCleanup: EffectCleanupRegisterFn): void {
		// Validate
		if (this.autoLoopInterval() <= 0 || this.listData().pages <= 1) {
			return;
		}

		// Loop through pages
		const loopInterval = setInterval(() => {
			this.goNext({allowLoop: true});
			this.loopIntervalPercentage.set(100);
		}, this.autoLoopInterval());

		// Update progress spinner percentage
		const intervalsInLoop = this.autoLoopInterval() / 100;
		const percentageDecreasePerInterval = 100 / intervalsInLoop;
		const percentageCounterInterval = setInterval(() => {
			this.loopIntervalPercentage.update((current: number) => current - percentageDecreasePerInterval);
		}, 100);

		// Cleanup
		onCleanup(() => {
			clearInterval(loopInterval);
			clearInterval(percentageCounterInterval);
		});
	}
}
